import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GiHamburgerMenu } from 'react-icons/gi';
import { MdOutlineRestaurantMenu } from 'react-icons/md';
import images from '../../../constants/images';
import LanguageDropdown from '../LanguageDropdown/LanguageDropdown';
import { LoginRecommendationModal } from '../../../container/ClientPage';
import { isAuthenticated } from '../../../utils/authUtils';
import { logout } from '../../../api/authApi';

import '../css/Navbar.css';

const Navbar = () => {
  const [toggleMenu, setToggleMenu] = React.useState(false);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [isAuth, setIsAuth] = React.useState(isAuthenticated());
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const languageDropdownRef = React.useRef();
  
  const goToHash = (hash) => {
    setShowDropdown(false); // Hide dropdown when navigating
    window.location.href = `/#${hash}`;
  };
  
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Update auth state when component mounts or location changes
  React.useEffect(() => {
    setIsAuth(isAuthenticated());
    
    // Show login recommendation modal only for non-authenticated users
    // and only if they haven't seen it before in this session
    if (!isAuthenticated() && !sessionStorage.getItem('loginModalShown')) {
      const timer = setTimeout(() => {
        setShowLoginModal(true);
        sessionStorage.setItem('loginModalShown', 'true');
      }, 2000); // Show modal after 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleLogout = () => {
    logout();
    setIsAuth(false);
    setShowDropdown(false);
    navigate('/');
    window.location.reload(); // Refresh to update auth state across the app
  };

  const handleViewBookings = () => {
    setShowDropdown(false);
    navigate('/profile');
  };

  const toggleDropdown = () => {
    // Close language dropdown if it's open
    if (languageDropdownRef.current && languageDropdownRef.current.closeDropdown) {
      languageDropdownRef.current.closeDropdown();
    }
    setShowDropdown(!showDropdown);
  };

  const handleNavigation = (path) => {
    setShowDropdown(false); // Hide dropdown when navigating
    navigate(path);
  };

  const handleLanguageDropdownOpen = () => {
    // Close user dropdown when language dropdown opens
    setShowDropdown(false);
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  return (
    <>
      <nav className="app__navbar">
        <div className="app__navbar-logo" onClick={() => handleNavigation('/')}>
          <img src={images.pho1986} alt="app__logo" />
        </div>
        <ul className="app__navbar-links">
          <li className="p__opensans"><a href="#home" onClick={(e) => { e.preventDefault(); goToHash('home'); }}>{t('navbar.home')}</a></li>
          <li className="p__opensans"><a href="#about" onClick={(e) => { e.preventDefault(); goToHash('about'); }}>{t('navbar.about')}</a></li>
          <li className="p__opensans"><a href="#menu" onClick={(e) => { e.preventDefault(); goToHash('menu'); }}>{t('navbar.menu')}</a></li>
          <li className="p__opensans"><a href="#boooking" onClick={(e) => { e.preventDefault(); goToHash('booking'); }}>{t('navbar.booking')}</a></li>
          <li className="p__opensans"><a href="#contact" onClick={(e) => { e.preventDefault(); goToHash('contact'); }}>{t('navbar.contact')}</a></li>
        </ul>
        <div className="app__navbar-login">
          {!isAuth && (
            <a href="#login" className="p__opensans" onClick={(e) => { e.preventDefault(); handleNavigation('/signin'); }}>{t('navbar.login')}</a>
          )}
          <a href="#booking" className="p__opensans" onClick={(e) => { e.preventDefault(); goToHash('booking'); }}>{t('navbar.bookTable')}</a>     
        </div>
        {isAuth && (
            <div className="logged-in-user-container">
              <div className="logged-in-icon" onClick={toggleDropdown}>
                <img src='/icons/user.png' alt="logged-in" />
              </div>
              {showDropdown && (
                <div className="user-dropdown">
                  <div className="user-dropdown-item" onClick={handleViewBookings}>
                    {t('navbar.viewBookings') || 'View Bookings'}
                  </div>
                  <div className="user-dropdown-item" onClick={handleLogout}>
                    {t('navbar.signOut') || 'Sign out'}
                  </div>
                </div>
              )}
            </div>
          )}
        <div className="navbar-language-wrapper">
          <LanguageDropdown 
            ref={languageDropdownRef}
            onDropdownOpen={handleLanguageDropdownOpen}
          />
        </div>
        <div className="app__navbar-smallscreen">
          <GiHamburgerMenu color="#fff" fontSize={27} onClick={() => setToggleMenu(true)} />
          {toggleMenu && (
            <div className="app__navbar-smallscreen_overlay flex__center slide-bottom">
              <MdOutlineRestaurantMenu fontSize={27} className="overlay__close" onClick={() => setToggleMenu(false)} />
              <ul className="app__navbar-smallscreen_links">
                <li><a href="#home" onClick={(e) => { e.preventDefault(); setToggleMenu(false); goToHash('home'); }}>{t('navbar.home')}</a></li>
                <li><a href="#about" onClick={(e) => { e.preventDefault(); setToggleMenu(false); goToHash('about'); }}>{t('navbar.about')}</a></li>
                <li><a href="#menu" onClick={(e) => { e.preventDefault(); setToggleMenu(false); goToHash('menu'); }}>{t('navbar.menu')}</a></li>
                <li><a href="#contact" onClick={(e) => { e.preventDefault(); setToggleMenu(false); goToHash('contact'); }}>{t('navbar.contact')}</a></li>
                <li><a href="#booking" onClick={(e) => { e.preventDefault(); setToggleMenu(false); goToHash('booking'); }}>{t('navbar.bookTable')}</a></li>
                {!isAuth && (
                  <li><a href="#login" onClick={(e) => { e.preventDefault(); setToggleMenu(false); navigate('/signin'); }}>{t('navbar.login')}</a></li>
                )}
                {isAuth && (
                  <>
                    <li><a href="#profile"onClick={(e) => { e.preventDefault(); setToggleMenu(false); navigate('/profile'); }}>{t('navbar.viewBookings') || 'View Bookings'}</a></li>
                    <li><a href="#signout" onClick={(e) => { e.preventDefault(); setToggleMenu(false); handleLogout(); }}>{t('navbar.signOut') || 'Sign out'}</a></li>
                  </>
                )}
              </ul>
            <div className="mobile-language-item">
              <LanguageDropdown />
            </div>
            </div>
          )}
        </div>
      </nav>
      
      {/* Login Recommendation Modal */}
      <LoginRecommendationModal 
        isVisible={showLoginModal} 
        onClose={handleCloseLoginModal}
      />
    </>
  );
};

export default Navbar;